import { computed, onMounted, onUnmounted, ref, watch, type Ref } from 'vue'

interface UseTrainingLoadingStateOptions {
  trainingDoneAndClosed: Ref<boolean>
  getCurrentUser: Ref<Record<string, any> | null>
  gotDataForVisualisation?: Ref<string[]>
  responseData?: Ref<Record<string, any> | null>
  expectedVisualisationCount?: number
}

export const useTrainingLoadingState = ({
  trainingDoneAndClosed,
  getCurrentUser,
  responseData,
  gotDataForVisualisation,
  expectedVisualisationCount = 2
}: UseTrainingLoadingStateOptions) => {
  const removeLoading = ref(false)
  const currentTime = ref(new Date())

  const hasAllVisualisationData = computed(() => {
    if (!gotDataForVisualisation) {
      return true
    }

    return gotDataForVisualisation.value.length === expectedVisualisationCount
  })

  const canShowResults = computed(() => {
    const isOnlyViewer = Boolean(getCurrentUser.value?.isOnlyViewer)
    return isOnlyViewer || trainingDoneAndClosed.value
  })

  watch(
    [canShowResults, hasAllVisualisationData],
    () => {
      removeLoading.value = hasAllVisualisationData.value && canShowResults.value
    },
    { deep: true }
  )

  const countdownText = computed(() => {
    const closingDateTime = responseData?.value?.trainingclosingdateandtime

    if (!closingDateTime) {
      return ''
    }

    const closingDate = new Date(closingDateTime)
    const difference = closingDate.getTime() - currentTime.value.getTime()

    if (difference <= 0) {
      return ''
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24))
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((difference % (1000 * 60)) / 1000)

    const parts: string[] = []
    if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`)
    if (hours > 0) parts.push(`${hours} hour`)
    if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`)
    if (parts.length === 0 && seconds > 0) {
      parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`)
    }

    return parts.length > 0 ? `Still ${parts.join(', ')} to fill your contribution to the FPS score` : ''
  })

  let countdownInterval: ReturnType<typeof setInterval> | null = null

  onMounted(() => {
    if (responseData) {
      countdownInterval = setInterval(() => {
        currentTime.value = new Date()
      }, 1000)
    }
  })

  onUnmounted(() => {
    if (countdownInterval) {
      clearInterval(countdownInterval)
    }
  })

  return {
    removeLoading,
    countdownText,
    canShowResults
  }
}
